/**
 * @file 账户效果总览
 * @author gaoxunchen
 */

import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { Select, DatePicker } from '@tencent/spaui';
import { Chart } from '@tencent/ptp-fe-common';
import dayjs from 'dayjs';

import { ACCOUNT_TYPE } from '@/constants/Constants';
import { currencyFormatV2 } from '@/pages/agency/utils/format';
import globalStore from '@/stores';

import { getEffect } from '../../api';
import { GranularityType, AgencyType, CHART_COLOR } from '../../constants';
import HomeItem from '../home-item';
import AgencSelect from '../agency-select';

import './index.scoped.less';

const date = dayjs();

const DATA_INDEX_LIST = [
  { label: '消耗', value: 'total_cost' },
  { label: '曝光', value: 'view_count' },
  { label: '点击量', value: 'valid_click_count' },
  { label: '点击率', value: 'ctr' },
  { label: '点击均价(元)', value: 'cpc' },
  { label: 'eCPM', value: 'ecpm' },
];

const CardAccountEffect: React.FC<any> = (props) => {
  const { title } = props;
  const { agency_uid, bm_id, agency_list: _agency_list, account_type } = globalStore.account;
  const agency_list = _agency_list
    .sort((a: any, b: any) => Number(a.parent_agency_uid) - Number(b.parent_agency_uid))
    .map((v: any, i: number) => ({
      ...v,
      color: CHART_COLOR[i % CHART_COLOR.length],
    }));
  const parentAgencyList = agency_list.filter((v: any) => !v.parent_agency_uid);
  const childAgencyList = agency_list.filter((v: any) => v.parent_agency_uid);
  const [begin_date, setBeginDate] = useState(date.format('YYYY-MM-DD'));
  const [end_date, setEndDate] = useState(date.format('YYYY-MM-DD'));
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [dataIndex, setDataIndex] = useState<string>(_.get(DATA_INDEX_LIST, '0.value', 'total_cost'));
  const [selectedParentAgencyList, setSelectedParentAgencyList] = useState<number[]>((
    parentAgencyList
      .filter((v: any) => v.ad_platform_type === 1)
      .map((v: any) => v.agency_uid)
  ));
  const [selectedChildAgencyList, setSelectedChildAgencyList] = useState<number[]>([]);

  useEffect(() => {
    getData();
  }, [
    begin_date,
    end_date,
  ]);

  const getData = async () => {
    const parmas = {
      agency_uid,
      bm_id,
      begin_date,
      end_date,
    };
    setData([]);
    try {
      const res = await getEffect({ data: parmas });
      const data = _.get(res, 'data.list', []);
      setData(agency_list.map((v) => ({
        ...(_.find(data, (x) => x.agency_uid === v.agency_uid) || {
          agency_uid: v.agency_uid,
          list: [],
        }),
        color: v.color,
      })));
    } catch (error) {}
  };

  const valueFormatter = (value: number | string, type: string) => {
    switch (type) {
      case 'total_cost':
      case 'cpc':
      case 'ecpm':
        return currencyFormatV2(Number(value), true);
        break;
      case 'view_count':
      case 'valid_click_count':
        return currencyFormatV2(Number(value));
        break;
    }
    return value;
  };

  const showData = agency_list
    .filter((v) => [
      ...selectedParentAgencyList,
      ...selectedChildAgencyList,
    ].includes(v.agency_uid))
    .map((v) => _.find(data, (x) => x.agency_uid === v.agency_uid) || null)
    .filter((v) => v);

  const option = {
    tooltip: {
      trigger: 'axis',
    },
    grid: {
      top: 30,
      left: 10,
      right: 10,
      bottom: 0,
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      axisLabel: {
        color: '#626366',
        fontSize: 10,
      },
      data: _.get(showData, '0.list', []).map((v: any) => {
        if (v.granularity_type === GranularityType.hour) {
          return `${(
            dayjs()
              .hour(v.granularity)
              .minute(0)
              .format('HH:mm')
          )}`;
        }
        return v.granularity;
      }),
    },
    yAxis: [
      {
        type: 'value',
        name: '数量',
        nameTextStyle: {
          color: '#626366',
          fontSize: 10,
        },
      },
      {
        type: 'value',
        name: '占比',
        nameTextStyle: {
          color: '#626366',
          fontSize: 10,
          formatter: '{value}%',
        },
        axisLabel: {
          color: '#626366',
          fontSize: 10,
          formatter: '{value}%',
        },
      },
    ],
    legend: {
      show: false,
    },
    series: showData.map((v: any) => ({
      type: 'line',
      smooth: true,
      name: v.agency_uid,
      itemStyle: {
        color: v.color,
      },
      areaStyle: {
        opacity: 0,
      },
      yAxisIndex: ['ctr'].includes(dataIndex) ? 1 : 0,
      tooltip: {
        valueFormatter: (value: number | string) => valueFormatter(value, dataIndex),
      },
      data: (v.list || []).map((v: any) => v[dataIndex] || 0, dataIndex),
    })),
  };

  return (
    <HomeItem
      title={title}
      canFold={true}
    >
      <div className="card-accout-effect">
        <div className="filter-group">
          <div className="filter-group-label">筛选</div>
          <div className="filter-group-content">
            <Select
              value={dataIndex}
              data={DATA_INDEX_LIST}
              onChange={(_event: any, value: string) => setDataIndex(value)}
            />
            <DatePicker
              type="range"
              startDate={begin_date}
              endDate={end_date}
              longestRange={365}
              horizontalPosition="left"
              onChange={(_event: any, startDate, endDate) => {
                setBeginDate(startDate);
                setEndDate(endDate);
              }}
            />
          </div>
        </div>
        <AgencSelect
          type={AgencyType.parent}
          data={parentAgencyList}
          value={selectedParentAgencyList}
          setValue={setSelectedParentAgencyList}
          granularityType={begin_date === end_date ? GranularityType.hour : GranularityType.day}
        />
        {account_type !== ACCOUNT_TYPE.direct ? (
          <AgencSelect
            type={AgencyType.child}
            data={childAgencyList}
            value={selectedChildAgencyList}
            setValue={setSelectedChildAgencyList}
            granularityType={begin_date === end_date ? GranularityType.hour : GranularityType.day}
          />
        ) : null}
        <Chart
          type="line"
          height="360px"
          option={option}
        />
      </div>
    </HomeItem>
  );
};

export default CardAccountEffect;
